/**
 * Upgrade Impact (spec sections 27 and 60).
 *
 * This is the panel most likely to mislead, so its wording is fixed rather than
 * left to the author of the day — in either language.
 *
 * Between any two Bear sessions a player usually changed several things at
 * once: a skill level, some gear, who they joined, how many troops they had.
 * The database records a *difference in outcome across a change window*, which
 * is an association. It is not, and cannot be, a measured effect of one change.
 *
 * So:
 * - `confoundedBy` is rendered before the number is interpreted, not after it.
 * - The verbs are the spec's: "associated with", "observed after", "likely
 *   contributor", "insufficient controlled samples" — in Korean, `~와 연관됨`,
 *   `~ 이후 관측됨`, `기여 가능성`, `통제된 샘플 부족`. No "caused", "improved
 *   by", "gained", "because of", and specifically never `~때문에` or `~로 인해`,
 *   which assert causation the data cannot support.
 * - A low sample count on either side downgrades the language further,
 *   regardless of how large the percentage looks.
 *
 * `src/i18n/dictionaries.test.ts` pins all of the above for the Korean copy.
 */

import type { PersonalBearAnalysisDto } from '@kso/schemas';
import { Badge, Card, Notice } from '../../components/ui/primitives';
import { ConfidenceBadge, LOW_SAMPLE_THRESHOLD, SampleCount } from '../../components/inference/Inference';
import { formatCompact, formatDateTime, formatPercent } from '../../lib/format';
import { useT } from '../../i18n';
import type { Dictionary } from '../../i18n';

type UpgradeImpact = PersonalBearAnalysisDto['upgradeImpacts'][number];

export function UpgradeImpactPanel({ impacts }: { impacts: readonly UpgradeImpact[] }) {
  const t = useT();

  return (
    <Card title={t.upgradeImpact.title}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t.upgradeImpact.blurb}
      </p>

      {impacts.length === 0 ? (
        <p className="absent small" style={{ margin: 0 }}>
          {t.upgradeImpact.empty}
        </p>
      ) : (
        <div className="stack">
          {impacts.map((impact) => (
            <ImpactRow key={`${impact.description}-${impact.changedAt}`} impact={impact} />
          ))}
        </div>
      )}
    </Card>
  );
}

function ImpactRow({ impact }: { impact: UpgradeImpact }) {
  const t = useT();
  const confounded = impact.confoundedBy.length > 0;
  const thinSamples =
    impact.beforeSamples < LOW_SAMPLE_THRESHOLD || impact.afterSamples < LOW_SAMPLE_THRESHOLD;

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: '10px 12px',
      }}
    >
      <div className="row">
        {/* The change description is the operator's own text; never translated. */}
        <strong>{impact.description}</strong>
        <span className="faint small">
          {t.upgradeImpact.recordedAt(formatDateTime(impact.changedAt) ?? t.common.notRecorded)}
        </span>
        <ConfidenceBadge level={impact.confidenceLevel} />
        {confounded ? (
          <Badge tone="danger" title={t.upgradeImpact.notIsolatedTitle}>
            {t.upgradeImpact.notIsolated}
          </Badge>
        ) : null}
      </div>

      <div className="row" style={{ marginTop: 8, gap: 20 }}>
        <div>
          <div className="stat__label">{t.upgradeImpact.medianBefore}</div>
          <div className="row" style={{ gap: 6 }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {impact.beforeMedian === null ? (
                <span className="absent">{t.upgradeImpact.notEnoughData}</span>
              ) : (
                formatCompact(impact.beforeMedian)
              )}
            </span>
            <SampleCount samples={impact.beforeSamples} noun={t.inference.nouns.sessions} />
          </div>
        </div>

        <div>
          <div className="stat__label">{t.upgradeImpact.medianAfter}</div>
          <div className="row" style={{ gap: 6 }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {impact.afterMedian === null ? (
                <span className="absent">{t.upgradeImpact.notEnoughData}</span>
              ) : (
                formatCompact(impact.afterMedian)
              )}
            </span>
            <SampleCount samples={impact.afterSamples} noun={t.inference.nouns.sessions} />
          </div>
        </div>

        <div>
          {/* "Difference observed after", not "effect of". */}
          <div className="stat__label">{t.upgradeImpact.differenceObserved}</div>
          <div style={{ fontVariantNumeric: 'tabular-nums', fontSize: 16, fontWeight: 600 }}>
            {impact.estimatedChange === null ? (
              <span className="absent">{t.common.notCalculable}</span>
            ) : (
              formatPercent(impact.estimatedChange)
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <Notice severity={confounded ? 'CRITICAL' : thinSamples ? 'WARN' : 'INFO'}>
          {interpretation(t, impact, confounded, thinSamples)}
        </Notice>
      </div>

      {confounded ? (
        <div style={{ marginTop: 8 }}>
          <div className="muted small">{t.upgradeImpact.alsoChanged}</div>
          <ul className="small" style={{ margin: '2px 0 0', paddingLeft: 18 }}>
            {impact.confoundedBy.map((confounder) => (
              <li key={confounder}>{confounder}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The mandated wording (spec section 27).
 *
 * Three cases, in descending order of what the evidence supports. None of them
 * ever says the change produced the difference.
 */
function interpretation(
  t: Dictionary,
  impact: UpgradeImpact,
  confounded: boolean,
  thinSamples: boolean,
): string {
  if (confounded) {
    return t.upgradeImpact.confounded(impact.description, impact.confoundedBy.length);
  }

  if (thinSamples) {
    return t.upgradeImpact.thin(
      impact.description,
      impact.beforeSamples < LOW_SAMPLE_THRESHOLD
        ? t.upgradeImpact.beforeSide
        : t.upgradeImpact.afterSide,
    );
  }

  return t.upgradeImpact.clean(impact.description);
}
